/**
 * Helper functions for building the Jira ticket content.
 */
import * as _ from 'lodash';
import * as moment from 'moment';
import { getIssueTypeLabel } from '../../../../../helpers/issuesV2';
import { UNABLE_TO_CALCULATE, NO_SAFE_VERSION } from '../../../../../../shared/vulns/remediation';
import { COUNT_MAX_FE, Category } from '../../../../../../shared/issuesV2';
import { plural } from '../../../../../../shared/helpers';
import type {
  LicensingIssue,
  QualityIssue,
  VulnIssue,
  Source,
  Issue,
} from '../../../../../../shared/issuesV2';
import { nuc } from '../../../../../utils';
import type { User } from '../../../../../types';

type TicketContent = {
  Title: `[FOSSA] ${string}`;
  Link: `[${string}](${string})`;
  H2: `## ${string}\n\n`;
  H3: `### ${string}\n\n`;
  Hr: `---\n`;
  Line: `${string}\n`;
  Bold: `**${string}**`;
  Italic: `_${string}_`;
  IssueSource: `${string} (${string})` | string;
  Panel: `{panel:title=${string}}\n${string}\n{panel}`;
};

const markdown = {
  title: (title: string): TicketContent['Title'] => `[FOSSA] ${title}`,
  link: (anchor: string, url: string): TicketContent['Link'] => `[${anchor}](${url})`,
  h2: (text: string): TicketContent['H2'] => `## ${text}\n\n`,
  h3: (text: string): TicketContent['H3'] => `### ${text}\n\n`,
  bold: (text: string): TicketContent['Bold'] => `**${text}**`,
  italic: (text: string): TicketContent['Italic'] => `_${text}_`,
  hr: (): TicketContent['Hr'] => `---\n`,
  line: (text = ''): TicketContent['Line'] => `${text}\n`,
  // eslint-disable-next-line lodash/prefer-lodash-chain
  lines: (text: string, count: number): string => `${markdown.line(text)}${_.range(0, count - 1).map(() => '').join('\n')}`,
  issueSource: (source: Issue['source']): TicketContent['IssueSource'] => `${source.name}${source.version ? ` (${source.version})` : ''}`,
  issueHeading: () => markdown.h3('Issue'),
  //  Renders an info panel in Jira, using Atlassian's funky markdown.
  panel: (title: string, content: string): TicketContent['Panel'] => `{panel:title=${title}}\n${content}\n{panel}`,
};

/**
 * Displays the warning when the user has more than 50 issues selected for export.
 *
 * @param {number} selectedIssuesCount -Count of selected issues.
 */
const FEMaxCountWarning = (selectedIssuesCount: number) =>
  markdown.lines(markdown.bold(`There are ${selectedIssuesCount} selected issues. Ticket content will just include information of the first ${COUNT_MAX_FE} issues.`), 3);

/**
 * Helper for building the title of the exporting ticket.
 *
 * @param {Issue[]} issues - Issues we are creating a ticket for. If on issue details, this always contains just one issue.
 * @param {number} selectedIssuesCount - The number of issues selected. If on issue details, this is always 1.
 */
export const Title = (issues: Issue[], selectedIssuesCount = 1): TicketContent['Title'] => {
  //  Get a count of the various packages where the issues come from.
  const sourcesCount = Object.keys(_.groupBy(issues, issue => issue.source.id)).length;
  if (sourcesCount > 1) {
    const title = markdown.title(`${selectedIssuesCount} Issues found`);
    //  If we have everything selected, we can include the number of dependencies.
    if (selectedIssuesCount === issues.length) {
      return `${title} in ${sourcesCount} dependencies`;
    }
    return title;
  }
  const issue = _.head(issues) as Issue;
  return markdown.title(`Issue with ${markdown.issueSource(issue.source)} - ${getIssueTypeLabel(issue.type)}`);
};

/**
 * Determines the link back to the issue in FOSSA, based on the current URL.
 * If we are on the issue details page, there is no need to append
 * the issue ID to the URL, but on the issues inbox we should.
 *
 * @param {number} issueId - The ID to the issue.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 */
const ViewOnFossaLink = (
  issueId: number,
  location: Location,
  includeIssueId = false,
): TicketContent['Link'] => {
  const anchorText = `View ${includeIssueId ? `issue #${issueId}` : 'issue'} on FOSSA`;

  // Ensure that the "View on FOSSA" link points to a specific issue. In some situations,
  // the issue ID already be present in the URL (project / RG issue details, for example)
  // but in others (any issue list) it will NOT be present. This check adds the issue ID
  // to the issue link only if it isn't already there.
  if (!location.pathname.endsWith(`/${issueId}`)) {
    return markdown.link(anchorText, `${location.origin}${location.pathname}/${issueId}`);
  }

  return markdown.link(anchorText, `${location.origin}${location.pathname}`);
};

/**
 * Renders a list of ViewOnFossaLinks
 *
 * @param {Issue[]} issues - Issues we are creating a ticket for.
 */
const ViewOnFossaLinks = (issues: Issue[], location: Location): string => {
  if (issues.length === 1) {
    const issue = _.head(issues) as Issue;
    return markdown.lines(ViewOnFossaLink(issue.id, location), 3);
  }

  return [
    ...issues.map(issue => `- ${ViewOnFossaLink(issue.id, location, true)}`),
    markdown.line(),
  ].join(markdown.line());
};

/**
 * Helper for building the top part of the content section of an exporting ticket.
 *
 * @param {Issue} issue - Issues we are creating a ticket for. If on issue details, this always contains just one issue.
 */
const Header = (issue: Issue): string =>
  markdown.h2(`${getIssueTypeLabel(issue.type)} - ${markdown.issueSource(issue.source)}`);

/**
 * Renders the Package URL section, assuming the package
 * has a URL which can be rendered.
 *
 * @param {Souce} source - Issue source.
 */
const PackageURL = (source?: Source): string => {
  if (!source?.url) {
    return '';
  }
  return markdown.h3('Package URL') + markdown.lines(markdown.link(source.name, source.url), 3);
};

/**
 * Takes a list of issues and returns the Affected Projects section.
 * Removes duplicate project entries and renders each project as
 * a link on a new link.
 *
 * @param {Issue[]} issues - Issues we are creating a ticket for. If on issue details, this always contains just one issue.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 */
const ProjectLinks = (issues: Issue[], location: Location): string => {
  const heading = markdown.h3('Affected Projects');
  const projects = issues.flatMap(issue => issue.projects);

  if (!projects.length) {
    return '';
  }

  const uniqueProjects = _.uniqBy(projects, proj => proj.id);
  return uniqueProjects.reduce<string>((acc, project) => (
    markdown.line(`${acc}- ${markdown.link(project.title, `${location.origin}/projects/${nuc(project.id)}`)}`)
  ), heading);
};

/**
 * Renders the section which displays the licenses
 * for Licensing issues.
 *
 * @param {LicensingIssue[]} issues - List of licensing issues.
 */
const LicenseSection = (issues: LicensingIssue[]): string => {
  const licenses = issues
    .filter(({ license }) => license)
    .map(({ license }) => markdown.line(`- ${license}`));

  if (!licenses.length) {
    return markdown.lines('', 2) + markdown.line(markdown.bold('No licenses found by FOSSA.'));
  }
  return markdown.lines('', 2) + markdown.h3('Licenses found by FOSSA') + licenses.join('');
};

/**
 * Returns the policy content for the licensing issue.
 *
 * @param {LicensingIssue} issue - A given licensing issue.
 */
const IssueLicensingContent = (issue: LicensingIssue): string => {
  switch (issue.type) {
    case 'policy_conflict':
      return markdown.line(`Your policy disallows dependencies licensed under the ${issue.license} license.`);
    case 'policy_flag':
      return markdown.line(`Your policy has flagged the ${issue.license} license for review.`);
    case 'unlicensed_dependency':
    default:
      return markdown.line('This dependency hasn\'t specified a license. You may need to contact the author to obtain permission to use it.');
  }
};

/**
 * Renders content for LicensingIssues.
 *
 * @param {LicensingIssue[]} issues - List of licensing issues.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 */
const IssueLicensing = (issues: LicensingIssue[], location: Location): string => {
  const issue = _.head(issues) as LicensingIssue;
  return [
    Header(issue),
    IssueLicensingContent(issue),
    markdown.lines('', 2),
    ViewOnFossaLinks(issues, location),
    PackageURL(issue.source),
    ProjectLinks(issues, location),
    LicenseSection(issues),
    markdown.lines('', 2),
  ].join('');
};

/**
 * Builds the main ticket content for licensing issues,
 * doing so by grouping by issue type.
 *
 * @param {LicensingIssue} ungroupedIssues - List of licening issues.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 */
const LicensingIssuesByType = (
  ungroupedIssues: LicensingIssue[],
  location: Location,
) =>
  // eslint-disable-next-line you-dont-need-lodash-underscore/map
  _.map(_.groupBy(ungroupedIssues, issue => issue.type), (issues: LicensingIssue[]) => IssueLicensing(issues, location)).join('');

/**
 * Displays the number of discovered vulns.
 *
 * @param {number} issuesCount - The number of vulns issues.
 */
const VulnIssueCount = (issuesCount: number): string =>
  markdown.lines(markdown.bold(`Discovered ${issuesCount} ${plural(issuesCount, 'Vulnerability', 'Vulnerabilities')}:`), 3);

/**
 * Renders the core content for a vulns issue.
 *
 * @param {VulnIssue} issue - A given VulnIssue
 * @param {Location} location - window.location, passed in to make unit testing easier.
 */
const IssueVulnContent = (issue: VulnIssue, location: Location): string => {
  let text = markdown.line(`- ${markdown.bold(issue.cve)}`);
  text += markdown.line(`-- Severity: ${_.capitalize(issue.severity)}${issue.cvss ? `(${issue.cvss})` : ''}`);
  if (issue.remediation && issue.remediation.partialFix === NO_SAFE_VERSION) {
    text += markdown.line(`-- Remediation: No Fix`);
  } else if (issue.remediation && issue.remediation.partialFix !== UNABLE_TO_CALCULATE) {
    text += markdown.line(`-- Remediation: Upgrade to ${issue.remediation.partialFix}`);
  }
  text += markdown.panel(`${issue.cve} Details`, issue.details || 'No details available.');
  text += markdown.lines('', 3);
  text += markdown.line(ViewOnFossaLink(issue.id, location));
  return text;
};

/**
 * Renders content for VulnIssues.
 *
 * @param {VulnIssue[]} issues - Vulnerability issues.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 */
const IssueVuln = (issues: VulnIssue[], location: Location) => {
  const issue = _.head(issues) as VulnIssue;
  return [
    Header(issue),
    PackageURL(issue.source),
    VulnIssueCount(issues.length),
    issues.map(vulnIssue => IssueVulnContent(vulnIssue, location)).join(markdown.line()),
    markdown.line(),
  ].join('');
};

/**
 * Renders the quality issue details
 *
 * @param {QualityIssue} issue - A quality issue.
 */
const IssueQualityContent = (issue: QualityIssue): string => {
  switch (issue.type) {
    case 'blacklisted_dependency':
      return markdown.line(`Your policy has blocked the ${issue.source.name || issue.source.id} dependency.`);
    case 'outdated_dependency':
      return markdown.line('These dependencies have been flagged as outdated by your policy. This may either be an out of date Major, Minor, or Patch version.');
    case 'risk_empty-package':
      return markdown.line('These dependencies have no reasonable source code and are considered empty.');
    case 'risk_native-code':
      return markdown.line('These dependencies contain compiled executables that are not expected within their ecosystem.');
    case 'risk_abandonware':
    default:
      return markdown.line('These dependencies have not received updates in over two years and may have been abandoned.');
  }
};

/**
 * Renders content for QualityIssues.
 *
 * @param {QualityIssue[]} issues - Quality issues.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 */
const IssueQuality = (issues: QualityIssue[], location: Location): string => {
  const issue = _.head(issues) as QualityIssue;
  return [
    Header(issue),
    IssueQualityContent(issue),
    markdown.line(),
    PackageURL(issue.source),
    ViewOnFossaLinks(issues, location),
    ProjectLinks(issues, location),
  ].join('');
};

/**
 * Renders the footer portion of the ticket content.
 *
 * @param {Location} location - window.location, passed in to make unit testing easier.
 * @param {User} user - window.user, passed in to make unit testing easier.
 */
const Footer = (location: Location, user: User): string => [
  markdown.hr(),
  markdown.line(
    `Generated by ${markdown.link('FOSSA', location.origin)} on ${moment().format('L')}`,
  ),
  markdown.lines(`Ticket generated by FOSSA user: ${user.username || user.email}`, 2),
].join('\n');

/**
 * Main function which will take the issues, group them by source (ie: where'd they come from)
 * and then depending on the category, building the content for the issue ticket.
 *
 * @param {Issue[]} issues - List of issues. Note that for issue details this is just one issue.
 * @param {Category} issueCategory - Current issue Category.
 * @param {number} selectedIssuesCount - Number of selected issues. Note that for issue details this is just one issue.
 * @param {Location} location - window.location, passed in to make unit testing easier.
 * @param {User} user - window.user, passed in to make unit testing easier.
 */
export const Content = (
  issues: Issue[],
  issueCategory: Category,
  selectedIssuesCount: number,
  location: Location,
  user: User,
) => {
  let ticketContent = selectedIssuesCount > issues.length
    ? FEMaxCountWarning(selectedIssuesCount)
    : '';
  const issuesByDependencies = _.groupBy(issues, issue => issue.source.id);
  switch (issueCategory) {
    case Category.Licensing:
      ticketContent +=
        // Iterating over a collection. Cannot use Array.map
        // eslint-disable-next-line you-dont-need-lodash-underscore/map
        _.map(issuesByDependencies, (groupedIssues: LicensingIssue[]) => LicensingIssuesByType(groupedIssues, location)).join('\n\n');
      break;

    case Category.Vulnerability:
      // Iterating over a collection. Cannot use Array.map
      // eslint-disable-next-line you-dont-need-lodash-underscore/map
      ticketContent += _.map(issuesByDependencies, (groupedIssues: VulnIssue[]) => IssueVuln(groupedIssues, location)).join('\n\n');
      break;

    case Category.Quality:
    default:
      ticketContent +=
        // Iterating over a collection. Cannot use Array.map
        // eslint-disable-next-line you-dont-need-lodash-underscore/map
        _.map(issuesByDependencies, (groupedIssues: QualityIssue[]) => IssueQuality(groupedIssues, location)).join('\n\n');
      break;
  }
  ticketContent += Footer(location, user);
  return ticketContent;
};

//  These exports are just so we can unit test them,
//  and they should not be imported by application code.
//  eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention
export const _testing = {
  FEMaxCountWarning,
  Title,
  ViewOnFossaLink,
  Header,
  PackageURL,
  ProjectLinks,
  LicenseSection,
  IssueLicensingContent,
  LicensingIssuesByType,
  VulnIssueCount,
  IssueVulnContent,
  IssueVuln,
  IssueQualityContent,
  IssueQuality,
  Footer,
};
